import React, { useState, useEffect } from 'react';
import { 
  Target, Trophy, Clock, Users, Star, 
  Award, TrendingUp, Calendar, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Navbar from './Navbar';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MyContests = () => {
  const [contests, setContests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchUserContests();
    fetchUserStats();
  }, []);

  const fetchUserContests = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user/contests`);
      setContests(response.data);
    } catch (error) {
      console.error('Failed to fetch contests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const getStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'joined':
        return <Badge className="bg-blue-100 text-blue-800">Joined</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'active':
        return <Badge className="bg-orange-100 text-orange-800">Active</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const getContestStatusIcon = (status, contest) => {
    const now = new Date();
    const startTime = new Date(contest?.contest?.start_datetime);
    const endTime = new Date(contest?.contest?.end_datetime);

    if (status === 'completed') return <Award className="h-5 w-5 text-green-500" />;
    if (now < startTime) return <Clock className="h-5 w-5 text-blue-500" />;
    if (now >= startTime && now <= endTime) return <Target className="h-5 w-5 text-red-500" />;
    return <Clock className="h-5 w-5 text-gray-400" />;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredContests = contests.filter(contest => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'ongoing') {
      const now = new Date();
      const startTime = new Date(contest.contest?.start_datetime);
      const endTime = new Date(contest.contest?.end_datetime);
      return now >= startTime && now <= endTime;
    }
    if (activeFilter === 'completed') {
      return ['completed'].includes(contest.user_contest?.status);
    }
    return contest.user_contest?.status === activeFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Contests</h1>
          <p className="text-gray-600">Track your contest participation and performance</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Total Contests</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_contests}</p>
                  </div>
                  <Target className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Completed</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.completed_contests}</p>
                  </div>
                  <Award className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Participation Rate</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_contests ? ((stats.completed_contests / stats.total_contests) * 100).toFixed(1) : 0}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'all', label: 'All Contests' },
            { id: 'ongoing', label: 'Live Now' },
            { id: 'completed', label: 'Completed' }
          ].map((filter) => (
            <Button
              key={filter.id}
              variant={activeFilter === filter.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(filter.id)}
              className={activeFilter === filter.id ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {filter.label}
            </Button>
          ))}
        </div>

        {/* Contests List */}
        <div className="space-y-4">
          {filteredContests.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Target className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {activeFilter === 'all' ? 'No contests yet' : `No ${activeFilter} contests`}
                </h3>
                <p className="text-gray-600 mb-4">
                  {activeFilter === 'all' 
                    ? "Join your first contest to get started!" 
                    : `You don't have any ${activeFilter} contests.`
                  }
                </p>
                <Button className="bg-red-600 hover:bg-red-700">
                  Browse Contests
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredContests.map((contestData) => {
              const contest = contestData.contest;
              const userContest = contestData.user_contest;
              
              if (!contest) return null;

              const now = new Date();
              const startTime = new Date(contest.start_datetime);
              const endTime = new Date(contest.end_datetime);
              const isLive = now >= startTime && now <= endTime;
              const isUpcoming = now < startTime;
              const isCompleted = now > endTime;

              return (
                <Card key={userContest.id} className={`hover:shadow-md transition-shadow ${
                  isLive ? 'border-red-200 bg-red-50' : ''
                }`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        {getContestStatusIcon(userContest.status, contestData)}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{contest.title}</h3>
                          <p className="text-gray-600 text-sm">{contest.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(userContest.status)}
                        {isLive && (
                          <Badge className="bg-red-600 text-white animate-pulse">
                            🔴 LIVE
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Type</p>
                        <p className="font-semibold text-gray-900">{contest.contest_type || 'Quiz'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Participants</p>
                        <p className="font-semibold text-gray-900">{contest.current_participants}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          {userContest.score ? 'Your Score' : 'Status'}
                        </p>
                        <p className="font-semibold text-gray-900">
                          {userContest.score || 'Registered'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        <div className="flex items-center space-x-4">
                          <span>Joined: {formatDate(userContest.joined_at)}</span>
                          {isUpcoming && (
                            <span>Starts: {formatDate(contest.start_datetime)}</span>
                          )}
                          {isLive && (
                            <span>Ends: {formatDate(contest.end_datetime)}</span>
                          )}
                          {isCompleted && userContest.completed_at && (
                            <span>Completed: {formatDate(userContest.completed_at)}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {isLive && userContest.status === 'joined' && (
                          <Button size="sm" className="bg-red-600 hover:bg-red-700">
                            Play Now
                          </Button>
                        )}
                        {isCompleted && (
                          <Button size="sm" variant="outline">
                            View Results
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Performance Analytics */}
        {stats && stats.total_contests > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Performance Analytics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Contest Performance</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Completion Rate:</span>
                      <span className="font-semibold text-green-600">
                        {stats.total_contests ? ((stats.completed_contests / stats.total_contests) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Average Score:</span>
                      <span className="font-semibold text-gray-900">N/A</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Participation:</span>
                      <span className="font-semibold text-gray-900">{stats.total_contests}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Recent Activity</h4>
                  <div className="space-y-2">
                    {stats.recent_transactions && stats.recent_transactions.length > 0 ? (
                      stats.recent_transactions.slice(0, 3).map((transaction, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{transaction.description}</span>
                          <span className="font-medium text-gray-600">
                            {new Date(transaction.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-500 text-sm">No recent activity</div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MyContests;